import {
  createContext, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import {
  onAuthStateChanged, signInWithEmailAndPassword, signOut,
  setPersistence, browserLocalPersistence, type User as FirebaseUser,
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import type { AppUser, Role } from '@/types';

type AuthState = {
  firebaseUser: FirebaseUser | null;
  user: AppUser | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOutUser: () => Promise<void>;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (cancelled) return;

      setFirebaseUser(fbUser);

      if (!fbUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        // Users collection is the source of truth for role + profile.
        // Owners provision new users via Settings → Users, which creates
        // both the Auth account and the `users/{uid}` document.
        const snap = await getDoc(doc(db, 'users', fbUser.uid));

        if (!snap.exists()) {
          // Auth record exists but no app profile → treat as unprovisioned.
          await signOut(auth);
          setError('Your account has not been provisioned. Contact an owner.');
          setUser(null);
          setLoading(false);
          return;
        }

        const data = snap.data() as Partial<AppUser>;

        if (data.disabled) {
          await signOut(auth);
          setError('Your account has been disabled.');
          setUser(null);
          setLoading(false);
          return;
        }

        setUser({
          uid: fbUser.uid,
          email: fbUser.email ?? '',
          displayName: fbUser.displayName ?? data.displayName ?? null,
          photoURL: fbUser.photoURL ?? null,
          role: (data.role as Role) ?? 'staff',
          department: data.department,
          createdAt: data.createdAt,
        });
        setError(null);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[auth] profile lookup failed', e);
        setError('Unable to load your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    });

    // Force re-login if the backend returns 401.
    const onExpired = () => void signOut(auth);
    window.addEventListener('auth:expired', onExpired);

    return () => {
      cancelled = true;
      unsub();
      window.removeEventListener('auth:expired', onExpired);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    setError(null);
    await setPersistence(auth, browserLocalPersistence);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (e: unknown) {
      const code = (e as { code?: string }).code;
      const msg =
        code === 'auth/invalid-credential' || code === 'auth/wrong-password'
          ? 'Invalid email or password.'
          : code === 'auth/too-many-requests'
          ? 'Too many attempts. Please wait a few minutes.'
          : code === 'auth/user-disabled'
          ? 'Your account has been disabled.'
          : 'Sign in failed. Please try again.';
      setError(msg);
      throw new Error(msg);
    }
  };

  const signOutUser = async () => {
    await signOut(auth);
  };

  const value = useMemo<AuthState>(
    () => ({ firebaseUser, user, loading, error, signIn, signOutUser }),
    [firebaseUser, user, loading, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
